import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Direct Google OAuth config — fetched once from /api/auth/google/config
  const [googleConfig, setGoogleConfig] = useState({ enabled: false, client_id: null });

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Google OAuth config (so we know whether to render the direct button)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/google/config`, { withCredentials: true })
      .then((r) => { if (!cancelled) setGoogleConfig(r.data || {}); })
      .catch(() => { /* direct flow unavailable, fall back to Emergent */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Legacy fallback (Emergent proxy) — only used when Google direct is disabled
  // (i.e. GOOGLE_CLIENT_ID not set on backend). Production should never hit this.
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/studio/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  /**
   * Direct Google login (popup flow).
   * Called by the `<GoogleLoginButton />` after a successful popup auth-code response.
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const exchangeGoogleCode = useCallback(async ({ code, redirect_uri }) => {
    const res = await axios.post(
      `${API}/auth/google/exchange`,
      { code, redirect_uri },
      { withCredentials: true }
    );
    // Refresh session — exchange already set the cookie, /me confirms the user
    await checkAuth();
    return res.data;
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const updateTokens = (newBalance) => {
    if (user) {
      setUser({ ...user, tokens: newBalance });
    }
  };

  const updateTheme = useCallback(async (theme) => {
    try { localStorage.setItem('shoebox_theme', theme); } catch {}
    if (user) setUser((prev) => prev ? { ...prev, theme } : prev);
    try {
      await axios.put(`${API}/auth/theme`, { theme }, { withCredentials: true });
    } catch {
      // ignore — landing page may be called pre-auth, localStorage still wins
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, updateUser, updateTokens, updateTheme, checkAuth,
      googleConfig, exchangeGoogleCode,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
